import React, { useState, useEffect } from "react";
import emailjs from "@emailjs/browser";
import { toast } from "sonner";
import { X } from "lucide-react";
import Lottie from "lottie-react";
import loader from "./../assets/Animation - 1735544240874.json";

const LeadPopup = () => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    number: "",
    services: "",
    message: "",
  });

  // Show popup on page load/reload always, and when user switches back to this tab
  useEffect(() => {
    // Show on load after 3 seconds
    const timer = setTimeout(() => {
      setOpen(true);
    }, 3000);

    // Show when user switches back to this tab
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        setOpen(true);
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    emailjs.init(import.meta.env.VITE_PROFILE_ID);
    emailjs
      .send(
        import.meta.env.VITE_SERVICE_ID,
        import.meta.env.VITE_TEMPLATE_ID_BUSINESS,
        formData,
        import.meta.env.VITE_PROFILE_ID
      )
      .then(
        () => {
          toast.success("Your message has been sent successfully!");
          setLoading(false);
          setOpen(false);
          setFormData({ name: "", email: "", number: "", services: "", message: "" });
        },
        () => {
          toast.error("Something went wrong. Please try again.");
          setLoading(false);
        }
      );
  };

  const handleClose = () => setOpen(false);

  if (!open) return null;

  return (
    // Backdrop
    <div
      className="fixed inset-0 z-[2147483647] flex items-center justify-center bg-black/60 backdrop-blur-sm px-4"
      onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
    >
      {/* Modal */}
      <div
        className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto"
        style={{ animation: "popupSlideIn 0.4s cubic-bezier(0.34,1.56,0.64,1) both" }}
      >
        {/* Top gradient bar */}
        <div className="h-2 w-full gradient" />

        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-red-100 hover:text-red-500 transition-colors z-10"
          aria-label="Close"
        >
          <X size={16} />
        </button>

        <div className="px-7 pt-6 pb-7">
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <Lottie animationData={loader} className="w-40" />
            </div>
          ) : (
            <>
              {/* Header */}
              <div className="mb-6">
                <span className="inline-block bg-[#f04c27]/10 text-[#f04c27] text-xs font-bold px-3 py-1 rounded-full mb-3">
                  FREE CONSULTATION
                </span>
                <h2 className="text-2xl font-bold text-[#363090] leading-tight">
                  Talk to Our <span className="text-[#f04c27]">ERP & CRM</span> Experts
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  Get a free workflow analysis.
                </p>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Name */}
                <div className="relative z-0 w-full group">
                  <input
                    type="text"
                    name="name"
                    id="popup_name"
                    value={formData.name}
                    className="block py-2.5 px-0 w-full text-sm text-gray-900 bg-transparent border-0 border-b-2 border-gray-300 appearance-none focus:outline-none focus:ring-0 focus:border-[#f04c27] peer"
                    placeholder=" "
                    required
                    onChange={handleChange}
                  />
                  <label
                    htmlFor="popup_name"
                    className="peer-focus:font-medium absolute text-sm text-gray-500 duration-300 transform -translate-y-6 scale-75 top-3 -z-10 origin-[0] peer-focus:text-[#f04c27] peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-6"
                  >
                    Your Name
                  </label>
                </div>

                {/* Email */}
                <div className="relative z-0 w-full group">
                  <input
                    type="email"
                    name="email"
                    id="popup_email"
                    value={formData.email}
                    className="block py-2.5 px-0 w-full text-sm text-gray-900 bg-transparent border-0 border-b-2 border-gray-300 appearance-none focus:outline-none focus:ring-0 focus:border-[#f04c27] peer"
                    placeholder=" "
                    required
                    onChange={handleChange}
                  />
                  <label
                    htmlFor="popup_email"
                    className="peer-focus:font-medium absolute text-sm text-gray-500 duration-300 transform -translate-y-6 scale-75 top-3 -z-10 origin-[0] peer-focus:text-[#f04c27] peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-6"
                  >
                    Email Address
                  </label>
                </div>

                {/* Phone */}
                <div className="relative z-0 w-full group">
                  <input
                    type="number"
                    name="number"
                    id="popup_number"
                    value={formData.number}
                    className="block py-2.5 px-0 w-full text-sm text-gray-900 bg-transparent border-0 border-b-2 border-gray-300 appearance-none focus:outline-none focus:ring-0 focus:border-[#f04c27] peer"
                    placeholder=" "
                    required
                    onChange={handleChange}
                  />
                  <label
                    htmlFor="popup_number"
                    className="peer-focus:font-medium absolute text-sm text-gray-500 duration-300 transform -translate-y-6 scale-75 top-3 -z-10 origin-[0] peer-focus:text-[#f04c27] peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-6"
                  >
                    Mobile Number
                  </label>
                </div>

                {/* Service Type */}
                <div className="relative z-0 w-full group">
                  <input
                    type="text"
                    name="services"
                    id="popup_services"
                    value={formData.services}
                    className="block py-2.5 px-0 w-full text-sm text-gray-900 bg-transparent border-0 border-b-2 border-gray-300 appearance-none focus:outline-none focus:ring-0 focus:border-[#f04c27] peer"
                    placeholder=" "
                    onChange={handleChange}
                  />
                  <label
                    htmlFor="popup_services"
                    className="peer-focus:font-medium absolute text-sm text-gray-500 duration-300 transform -translate-y-6 scale-75 top-3 -z-10 origin-[0] peer-focus:text-[#f04c27] peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-6"
                  >
                    Service Type
                  </label>
                </div>

                {/* Message */}
                <div className="relative z-0 w-full group">
                  <textarea
                    name="message"
                    id="popup_message"
                    value={formData.message}
                    rows={2}
                    className="block py-2.5 px-0 w-full text-sm text-gray-900 bg-transparent border-0 border-b-2 border-gray-300 appearance-none focus:outline-none focus:ring-0 focus:border-[#f04c27] peer resize-none"
                    placeholder="Briefly describe your project..."
                    onChange={handleChange}
                  />
                </div>

                {/* Buttons */}
                <div className="flex gap-3 pt-2">
                  <button type="submit" className="button flex-1 justify-center text-sm whitespace-nowrap">
                    Submit
                    <svg className="icon" viewBox="0 0 24 24" fill="currentColor">
                      <path fillRule="evenodd" d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25zm4.28 10.28a.75.75 0 000-1.06l-3-3a.75.75 0 10-1.06 1.06l1.72 1.72H8.25a.75.75 0 000 1.5h5.69l-1.72 1.72a.75.75 0 101.06 1.06l3-3z" clipRule="evenodd" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={handleClose}
                    className="px-5 py-2 rounded-full border-2 border-gray-200 text-gray-500 text-sm font-semibold hover:border-red-300 hover:text-red-500 transition-colors whitespace-nowrap"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>

      {/* Keyframe animation */}
      <style>{`
        @keyframes popupSlideIn {
          0%   { opacity: 0; transform: scale(0.85) translateY(30px); }
          100% { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default LeadPopup;
